import { TEXT_COLUMN_MASK } from './mask.js';
import { TEXT_EVIDENCE_HEIGHT, TEXT_EVIDENCE_VERSION, } from './types.js';
const MAGIC = [0x54, 0x47, 0x45, 0x32]; // TGE2
const HEADER_BYTES = 12;
const DEFAULT_MAX_WIDTH = 4096;
const DEFAULT_MAX_CROP_HEIGHT = 4096;
const DEFAULT_MAX_PACKED_BYTES = 64 * 1024;
function limits(options) {
    return {
        maxWidth: options.maxWidth ?? DEFAULT_MAX_WIDTH,
        maxCropHeight: options.maxCropHeight ?? DEFAULT_MAX_CROP_HEIGHT,
        maxPackedBytes: options.maxPackedBytes ?? DEFAULT_MAX_PACKED_BYTES,
    };
}
export function assertTextEvidence(evidence, options = {}) {
    const max = limits(options);
    if (evidence.version !== TEXT_EVIDENCE_VERSION)
        throw new Error(`unsupported evidence version: ${String(evidence.version)}`);
    if (!Number.isInteger(evidence.width) || evidence.width < 1 || evidence.width > Math.min(max.maxWidth, 0xffff))
        throw new RangeError('invalid evidence width');
    if (!Number.isInteger(evidence.cropHeight) || evidence.cropHeight < TEXT_EVIDENCE_HEIGHT || evidence.cropHeight > Math.min(max.maxCropHeight, 0xffff))
        throw new RangeError('invalid evidence cropHeight');
    if (!Number.isInteger(evidence.lineY) || evidence.lineY < 0 || evidence.lineY + TEXT_EVIDENCE_HEIGHT > evidence.cropHeight)
        throw new RangeError('invalid evidence lineY');
    if (!(evidence.anchorColumns instanceof Uint16Array))
        throw new TypeError('anchorColumns must be a Uint16Array');
    if (!(evidence.inkColumns instanceof Uint16Array))
        throw new TypeError('inkColumns must be a Uint16Array');
    if (evidence.anchorColumns.length !== evidence.width)
        throw new RangeError('anchorColumns length does not match width');
    if (evidence.inkColumns.length !== evidence.width)
        throw new RangeError('inkColumns length does not match width');
    for (let x = 0; x < evidence.width; x++) {
        const anchor = evidence.anchorColumns[x] ?? 0;
        const ink = evidence.inkColumns[x] ?? 0;
        if ((anchor & ~TEXT_COLUMN_MASK) !== 0)
            throw new RangeError('anchor column contains bits outside the 12px line');
        if ((ink & ~TEXT_COLUMN_MASK) !== 0)
            throw new RangeError('ink column contains bits outside the 12px line');
        if ((anchor & ~ink & TEXT_COLUMN_MASK) !== 0)
            throw new RangeError('anchorColumns are inconsistent with inkColumns');
    }
}
export function packedTextEvidenceByteLength(width) {
    return HEADER_BYTES + width * 4;
}
/** TGE2 wire format: 12-byte header, then little-endian anchor columns and ink columns. */
export function packTextEvidence(evidence, options = {}) {
    assertTextEvidence(evidence, options);
    const byteLength = packedTextEvidenceByteLength(evidence.width);
    const max = limits(options);
    if (byteLength > max.maxPackedBytes)
        throw new RangeError('packed evidence exceeds maxPackedBytes');
    const out = new Uint8Array(byteLength);
    out.set(MAGIC, 0);
    const view = new DataView(out.buffer, out.byteOffset, out.byteLength);
    view.setUint16(4, evidence.width, true);
    view.setUint16(6, evidence.cropHeight, true);
    view.setUint16(8, evidence.lineY, true);
    view.setUint16(10, 0, true);
    let offset = HEADER_BYTES;
    for (const column of evidence.anchorColumns) {
        view.setUint16(offset, column, true);
        offset += 2;
    }
    for (const column of evidence.inkColumns) {
        view.setUint16(offset, column, true);
        offset += 2;
    }
    return out;
}
export function unpackTextEvidence(bytes, options = {}) {
    const max = limits(options);
    if (bytes.byteLength < HEADER_BYTES)
        throw new RangeError('packed evidence is truncated');
    if (bytes.byteLength > max.maxPackedBytes)
        throw new RangeError('packed evidence exceeds maxPackedBytes');
    for (let i = 0; i < MAGIC.length; i++)
        if (bytes[i] !== MAGIC[i])
            throw new Error('invalid text evidence magic/version');
    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const width = view.getUint16(4, true);
    const cropHeight = view.getUint16(6, true);
    const lineY = view.getUint16(8, true);
    if (view.getUint16(10, true) !== 0)
        throw new Error('unsupported text evidence flags');
    const expectedLength = packedTextEvidenceByteLength(width);
    if (bytes.byteLength !== expectedLength)
        throw new RangeError(`packed evidence length must be ${expectedLength} bytes`);
    const anchorColumns = new Uint16Array(width);
    const inkColumns = new Uint16Array(width);
    let offset = HEADER_BYTES;
    for (let x = 0; x < width; x++) {
        anchorColumns[x] = view.getUint16(offset, true);
        offset += 2;
    }
    for (let x = 0; x < width; x++) {
        inkColumns[x] = view.getUint16(offset, true);
        offset += 2;
    }
    const evidence = { version: TEXT_EVIDENCE_VERSION, width, cropHeight, lineY, anchorColumns, inkColumns };
    assertTextEvidence(evidence, options);
    return evidence;
}
//# sourceMappingURL=wire.js.map